import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';

import { textVariants } from '@/components/text';

const defaultClasses = clsx([
  'rounded-rad-a gap-a group flex flex-row items-center justify-center transition-all border-none box-border whitespace-nowrap disabled:pointer-events-none outline-none focus:outline-none leading-none ring-inset',
  textVariants({ variant: 'button' }),
]);

export const buttonVariants = cva(defaultClasses, {
  variants: {
    variant: {
      primary: [
        'bg-button-primary-background-default text-button-primary-text ring-1 ring-transparent',
        'hover:bg-button-primary-background-hover',
        'active:bg-button-primary-background-pressed',
        'focus-visible:ring-button-primary-outline-focused focus-visible:ring-2',
        'disabled:opacity-30',
      ],
      secondary: [
        'ring-1 ring-button-secondary-outline-default text-button-secondary-text-default bg-transparent',
        'hover:ring-2 hover:ring-button-secondary-outline-hover hover:text-button-secondary-text-hover',
        'active:ring-2 active:ring-button-secondary-outline-pressed active:text-button-secondary-text-pressed',
        'focus-visible:ring-2 focus-visible:ring-button-secondary-outline-focused',
        'disabled:ring-button-secondary-outline-disabled disabled:text-button-secondary-text-disabled',
      ],
      tertiary: [
        'text-button-tertiary-text-default bg-transparent',
        'hover:text-button-tertiary-text-hover',
        'active:text-button-tertiary-text-pressed',
        'disabled:text-button-tertiary-text-disabled',
      ],
      icon: [
        '!px-b ring-1 ring-button-icon-outline-default bg-transparent',
        'hover:ring-2 hover:ring-button-icon-outline-hover',
        'focus-visible:ring-2 focus-visible:ring-button-icon-outline-focused',
        'active:ring-2 active:ring-button-icon-outline-pressed',
        'disabled:ring-button-icon-outline-disabled',
      ],
      navigation: [
        'hover:bg-gray-50 hover:text-pink xl:px-a',
        'focus-visible:ring-2 focus-visible:ring-pink-500',
      ],
      unstyled: [],
    },
    iconPosition: {
      left: 'flex-row',
      right: 'flex-row-reverse',
    },
  },
  defaultVariants: {
    variant: 'primary',
  },
});

export type ButtonVariants = VariantProps<typeof buttonVariants>;

export const buttonIconVariants = cva(['shrink-0'], {
  variants: {
    variant: {
      primary: 'transition-colors text-button-primary-icon-default',
      secondary: [
        'transition-colors',
        'text-button-secondary-icon-default',
        'group-hover:text-button-secondary-icon-hover',
        'group-active:text-button-secondary-icon-pressed',
        'group-disabled:text-button-secondary-icon-disabled',
      ],
      tertiary: [
        'transition-colors',
        'text-button-tertiary-icon-default',
        'group-active:text-button-tertiary-icon-pressed',
        'group-disabled:text-button-tertiary-icon-disabled',
        'group-focus-visible:text-pink-500',
      ],
      icon: [
        'transition-colors',
        'text-button-icon-icon-default',
        'group-hover:text-button-icon-icon-hover',
        'group-active:text-button-icon-icon-pressed',
        'group-disabled:text-button-icon-icon-disabled',
      ],
      navigation: '',
      unstyled: ['transition-colors'],
    },
    defaultVariants: {
      variant: 'primary',
    },
  },
});

export type ButtonIconVariants = VariantProps<typeof buttonIconVariants>;

export const buttonSizeVariants = cva([], {
  variants: {
    size: {
      sm: 'min-h-[2.5rem] py-a px-b',
      default: 'min-h-[3rem] lg:min-h-[3.5rem] py-b px-c',
    },
    defaultVariants: {
      size: 'default',
    },
  },
});

export type ButtonSizeVariants = VariantProps<typeof buttonSizeVariants>;
